export const COOKIE_CONSENT_STORAGE_KEY = 'kf-cookie-consent';
export const COOKIE_CONSENT_COOKIE_NAME = 'kf_cookie_consent';
export const COOKIE_CONSENT_VERSION = 1;
export const COOKIE_CONSENT_MAX_AGE_DAYS = 180;

export type CookieConsentPreferences = {
  necessary: true;
  analytics: boolean;
};

export type StoredCookieConsent = CookieConsentPreferences & {
  version: number;
  updatedAt: string;
};

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function parseStoredConsent(value: string | null | undefined): StoredCookieConsent | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Partial<StoredCookieConsent>;

    if (
      parsed.version !== COOKIE_CONSENT_VERSION ||
      typeof parsed.analytics !== 'boolean' ||
      parsed.necessary !== true
    ) {
      return null;
    }

    return {
      necessary: true,
      analytics: parsed.analytics,
      version: COOKIE_CONSENT_VERSION,
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

function readConsentCookie(): StoredCookieConsent | null {
  if (!isBrowser()) {
    return null;
  }

  const match = document.cookie.match(
    new RegExp(`(?:^|; )${COOKIE_CONSENT_COOKIE_NAME}=([^;]*)`),
  );

  return parseStoredConsent(match ? decodeURIComponent(match[1]) : null);
}

export function readStoredCookieConsent(): StoredCookieConsent | null {
  if (!isBrowser()) {
    return null;
  }

  const local = parseStoredConsent(window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY));

  if (local) {
    return local;
  }

  return readConsentCookie();
}

export function saveCookieConsent(
  preferences: CookieConsentPreferences,
): StoredCookieConsent | null {
  if (!isBrowser()) {
    return null;
  }

  const stored: StoredCookieConsent = {
    necessary: true,
    analytics: preferences.analytics,
    version: COOKIE_CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  };

  const serialized = JSON.stringify(stored);
  const maxAge = COOKIE_CONSENT_MAX_AGE_DAYS * 24 * 60 * 60;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';

  window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, serialized);
  document.cookie = `${COOKIE_CONSENT_COOKIE_NAME}=${encodeURIComponent(serialized)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;

  return stored;
}

function deleteCookie(name: string, domain?: string) {
  if (!isBrowser()) {
    return;
  }

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domainPart = domain ? `; Domain=${domain}` : '';

  document.cookie = `${name}=; Max-Age=0; Path=/${domainPart}; SameSite=Lax${secure}`;
}

export function clearAnalyticsCookies(measurementId?: string) {
  if (!isBrowser()) {
    return;
  }

  const suffix = measurementId?.replace(/^G-/, '').trim();
  const cookieNames = ['_ga', '_gid', '_gat'];

  if (suffix) {
    cookieNames.push(`_ga_${suffix}`, `_gat_gtag_${suffix}`);
  }

  const hostname = window.location.hostname;
  const domainCandidates = new Set<string | undefined>([
    undefined,
    hostname,
    `.${hostname}`,
  ]);

  const parts = hostname.split('.');
  if (parts.length > 2) {
    const rootDomain = `.${parts.slice(-2).join('.')}`;
    domainCandidates.add(rootDomain);
  }

  cookieNames.forEach((cookieName) => {
    domainCandidates.forEach((domain) => deleteCookie(cookieName, domain));
  });
}
